import React, { useState } from 'react';
import { Box, Container, Typography, TextField, Grid, Button, Alert, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { createProgramWithOwner } from '../../services/programService';

// Phase C.1 — the first-ever Program-creation UI. create_program_with_
// owner() (migration 036) is the only live Program-creation RPC; the
// caller becomes the new Program's sole Owner atomically. City/state are
// optional here (unlike a Season Team's required city/state/zip) — a
// Program's location is discovery metadata (program_service_areas), not
// identity, matching that table's own header note.
function CreateProgramInner() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', city: '', state: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError('Program name is required.');
      return;
    }
    setSaving(true);
    try {
      const program = await createProgramWithOwner({
        name: form.name,
        stateCode: form.state || null,
        city: form.city || null,
      });
      navigate(`/my-programs/${program.id}`, { state: { justCreated: true } });
    } catch (e2) {
      setError(e2.message || 'Could not create program.');
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Button onClick={() => navigate('/my-programs')} sx={{ textTransform: 'none', color: '#6a8aa8', mb: 1, pl: 0 }}>
        ← My Programs
      </Button>
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>Create Program</Typography>
      <Typography sx={{ color: '#9eb8d0', mb: 3 }}>
        A Program is the persistent organization above your Seasons, Divisions, and Teams — it stays in place year after year. You will become its Owner.
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Program name" value={form.name} onChange={set('name')} required
              placeholder="Fort Youth Tackle Football"
            />
          </Grid>
          <Grid item xs={8}>
            <TextField fullWidth label="City (optional)" value={form.city} onChange={set('city')} />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth label="State (optional)" value={form.state} onChange={set('state')}
              inputProps={{ maxLength: 2 }} placeholder="WI"
            />
          </Grid>
        </Grid>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Button
          type="submit" variant="contained" color="primary" disabled={saving}
          sx={{ mt: 3, textTransform: 'none', fontWeight: 800, px: 3 }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Create Program'}
        </Button>
      </Box>
    </Container>
  );
}

export default function CreateProgramPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      {/* Phase C.1a — the FFN landing page's "Create a Program" CTA
          navigates here unconditionally; offerCreateAccount/message give
          a cold-traffic visitor (arriving with no prior context) a clear
          choice instead of the generic athlete-page copy this component
          defaults to. */}
      <RequireAuth offerCreateAccount message="Sign in or create a free account to start a new Program.">
        <CreateProgramInner />
      </RequireAuth>
    </Box>
  );
}
